import { useEffect, useState } from 'react';
import { smartAssistantSettingsApi, type AgentActivityRow, type AgentStatusRow, type GeneralSettings, type WhatsAppStatus } from '../api/smartAssistantSettingsApi';
import { WhatsAppConnectionCard } from '../components/WhatsAppConnectionCard';
import { usePlatformPermissions } from '../../../hooks/usePlatformPermissions';
import { IntelligenceShell } from '../../intelligence/components/IntelligenceShell';

type TabKey = 'general' | 'whatsapp' | 'agents' | 'behavior' | 'notifications' | 'security';

const TABS: { key: TabKey; label: string; icon: string; soon?: boolean }[] = [
  { key: 'general', label: 'عام', icon: 'ri-settings-4-line' },
  { key: 'whatsapp', label: 'قناة واتساب', icon: 'ri-whatsapp-line' },
  { key: 'agents', label: 'الوكلاء', icon: 'ri-robot-2-line' },
  { key: 'behavior', label: 'السلوك', icon: 'ri-magic-line', soon: true },
  { key: 'notifications', label: 'الإشعارات', icon: 'ri-notification-3-line', soon: true },
  { key: 'security', label: 'الأمان', icon: 'ri-shield-check-line', soon: true },
];

function ComingSoon({ title }: { title: string }) {
  return (
    <div className="rounded-2xl bg-white border border-dashed border-gray-200 p-10 max-w-2xl text-center opacity-70 select-none">
      <i className="ri-time-line text-2xl text-gray-300" />
      <h3 className="mt-2 text-sm font-black text-gray-500">{title}</h3>
      <p className="mt-1 text-xs text-gray-400">قريبًا — هذا القسم غير قابل للإعداد حاليًا.</p>
    </div>
  );
}

function GeneralTab({ canUpdate }: { canUpdate: boolean }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [settings, setSettings] = useState<GeneralSettings | null>(null);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      const r = await smartAssistantSettingsApi.getGeneralSettings();
      setLoading(false);
      if (!r.ok) { setError('تعذر تحميل الإعدادات. حاول مرة أخرى.'); return; }
      setSettings(r.data);
    })();
  }, []);

  const save = async () => {
    if (!settings) return;
    setSaving(true); setMsg(''); setError('');
    const r = await smartAssistantSettingsApi.updateGeneralSettings(settings);
    setSaving(false);
    if (!r.ok) { setError('تعذر حفظ الإعدادات. حاول مرة أخرى.'); return; }
    setSettings(r.data);
    setMsg('تم حفظ الإعدادات ✅');
  };

  const resetSessions = async () => {
    setResetting(true); setMsg(''); setError('');
    const r = await smartAssistantSettingsApi.resetAllSessions();
    setResetting(false);
    setConfirmReset(false);
    if (!r.ok) { setError('تعذر إعادة تعيين الجلسات. حاول مرة أخرى.'); return; }
    const n = (r.data?.whatsapp_conversations ?? 0) + (r.data?.intelligence_conversations ?? 0);
    setMsg(`تم بدء المحادثات من جديد ✅ (${n} جلسة)`);
  };

  if (loading) return <div className="animate-pulse h-40 rounded-2xl bg-white border border-gray-100 max-w-2xl" />;
  if (!settings) {
    return (
      <div className="rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-xs font-semibold text-rose-700 max-w-2xl">{error || 'تعذر تحميل الإعدادات.'}</div>
    );
  }

  return (
    <div className="max-w-2xl space-y-4">
      <div className="rounded-2xl bg-white border border-gray-100 p-6 space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-black text-gray-900">حالة المساعد</h3>
            <p className="text-xs text-gray-500 mt-0.5">عند الإيقاف، يتوقف المساعد عن الرد التلقائي على العملاء.</p>
          </div>
          <button
            type="button"
            disabled={!canUpdate}
            onClick={() => setSettings({ ...settings, assistant_status: settings.assistant_status === 'on' ? 'off' : 'on' })}
            className={`relative w-11 h-6 rounded-full transition-colors disabled:opacity-40 ${settings.assistant_status === 'on' ? 'bg-emerald-500' : 'bg-gray-300'}`}
          >
            <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${settings.assistant_status === 'on' ? 'left-[22px]' : 'left-0.5'}`} />
          </button>
        </div>

        <div>
          <label className="block text-xs font-bold text-gray-600 mb-1.5">اسم المساعد</label>
          <input
            value={settings.assistant_name}
            disabled={!canUpdate}
            onChange={(e) => setSettings({ ...settings, assistant_name: e.target.value })}
            className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm disabled:bg-gray-50"
          />
        </div>

        {!canUpdate && <p className="text-xs text-gray-400">لديك صلاحية عرض فقط لهذه الإعدادات.</p>}
        {error && <p className="text-xs font-semibold text-rose-600">{error}</p>}
        {msg && <p className="text-xs font-semibold text-emerald-700">{msg}</p>}

        {canUpdate && (
          <button
            type="button"
            disabled={saving || resetting}
            onClick={() => void save()}
            className="px-4 py-2 rounded-xl bg-gray-900 text-white text-xs font-bold hover:bg-gray-800 disabled:opacity-50"
          >
            {saving ? 'جارٍ الحفظ…' : 'حفظ التغييرات'}
          </button>
        )}
      </div>

      {canUpdate && (
        <div className="rounded-2xl bg-white border border-rose-100 p-6 space-y-3">
          <div>
            <h3 className="text-sm font-black text-gray-900">إعادة تعيين المحادثات</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              يبدأ المساعد كل المحادثات من جديد: تُمسح ذاكرة الجلسة وسجل الردود الحالي، وتبقى أسماء العملاء وأرقامهم.
            </p>
          </div>
          {!confirmReset ? (
            <button
              type="button"
              disabled={resetting}
              onClick={() => { setConfirmReset(true); setMsg(''); setError(''); }}
              className="px-4 py-2 rounded-xl border border-rose-200 bg-rose-50 text-rose-700 text-xs font-bold hover:bg-rose-100 disabled:opacity-50"
            >
              Reset session
            </button>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                disabled={resetting}
                onClick={() => void resetSessions()}
                className="px-4 py-2 rounded-xl bg-rose-600 text-white text-xs font-bold hover:bg-rose-700 disabled:opacity-50"
              >
                {resetting ? 'جارٍ إعادة التعيين…' : 'تأكيد — ابدأ من جديد'}
              </button>
              <button
                type="button"
                disabled={resetting}
                onClick={() => setConfirmReset(false)}
                className="px-4 py-2 rounded-xl border border-gray-200 text-gray-600 text-xs font-bold hover:bg-gray-50 disabled:opacity-50"
              >
                إلغاء
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const KNOWN_CAPABILITIES = [
  { key: 'create_demo', label: 'إنشاء حساب تجريبي' },
  { key: 'extend_demo', label: 'تمديد فترة الديمو' },
  { key: 'lookup_account', label: 'الاستعلام عن الحساب' },
  { key: 'reset_access', label: 'إعادة تعيين الدخول' },
  { key: 'create_support_case', label: 'فتح حالة دعم' },
  { key: 'handoff_conversation', label: 'تحويل المحادثة' },
  { key: 'escalate_conversation', label: 'تصعيد المحادثة' },
  { key: 'update_lead', label: 'تحديث بيانات العميل' },
  { key: 'create_followup', label: 'إنشاء متابعة' },
];

const ROLE_LABELS: Record<string, string> = {
  sales: 'مبيعات',
  support: 'دعم',
  success: 'نجاح العملاء',
  triage: 'فرز',
  billing: 'فوترة',
};

function AgentsTab({ canManage }: { canManage: boolean }) {
  const [loading, setLoading] = useState(true);
  const [agents, setAgents] = useState<AgentStatusRow[]>([]);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [editAgent, setEditAgent] = useState<AgentStatusRow | null>(null);
  const [activityAgent, setActivityAgent] = useState<AgentStatusRow | null>(null);
  const [activity, setActivity] = useState<AgentActivityRow[]>([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const load = async () => {
    const r = await smartAssistantSettingsApi.getAgents();
    setLoading(false);
    if (!r.ok) { setError('تعذر تحميل الموظفين. حاول مرة أخرى.'); return; }
    setAgents(r.data);
  };

  useEffect(() => { void load(); }, []);

  const toggleStatus = async (a: AgentStatusRow) => {
    if (!canManage || saving) return;
    setSaving(true); setError(''); setMsg('');
    const next = a.status === 'active' ? 'paused' : 'active';
    const r = await smartAssistantSettingsApi.setAgentStatus(a.id, next);
    setSaving(false);
    if (!r.ok) { setError('تعذر تغيير حالة الموظف.'); return; }
    setMsg(next === 'active' ? `تم تفعيل ${a.name} ✅` : `تم إيقاف ${a.name} مؤقتًا ⏸️`);
    await load();
  };

  const openActivity = async (a: AgentStatusRow) => {
    setActivityAgent(a); setActivityLoading(true); setActivity([]);
    const r = await smartAssistantSettingsApi.getAgentActivity(a.id);
    setActivityLoading(false);
    if (r.ok) setActivity(r.data);
  };

  const saveAgent = async () => {
    if (!editAgent) return;
    setSaving(true); setFormError('');
    const r = await smartAssistantSettingsApi.updateAgent(editAgent.id, {
      name: editAgent.name,
      personality: editAgent.personality,
      mission: editAgent.mission,
      department: editAgent.department,
      owner_user_id: editAgent.owner_user_id,
      capabilities: editAgent.capabilities,
      policies: editAgent.policies,
      handoff_targets: editAgent.handoff_targets,
      channels: editAgent.channels,
    });
    setSaving(false);
    if (!r.ok) { setFormError('تعذر حفظ بيانات الموظف. تحقق من القيم وحاول مجددًا.'); return; }
    setEditAgent(null);
    setMsg('تم حفظ بيانات الموظف ✅');
    await load();
  };

  const [creating, setCreating] = useState(false);
  const [newAgent, setNewAgent] = useState<{ name: string; assistant_key: string }>({ name: '', assistant_key: '' });
  const createAgent = async () => {
    if (!canManage || saving || !newAgent.name.trim() || !newAgent.assistant_key.trim()) return;
    setSaving(true); setFormError(''); setMsg('');
    const r = await smartAssistantSettingsApi.createAgent({ name: newAgent.name.trim(), assistant_key: newAgent.assistant_key.trim() });
    setSaving(false);
    if (!r.ok) { setFormError('تعذر إنشاء الموظف. تأكد أن المعرّف إنجليزي وغير مكرر.'); return; }
    setCreating(false); setNewAgent({ name: '', assistant_key: '' });
    setMsg('تم إنشاء الموظف الذكي ✅ (متوقف — فعّله بعد ضبط قدراته)');
    await load();
  };

  const toggleCap = (cap: string) => {
    if (!editAgent) return;
    const caps = editAgent.capabilities.includes(cap)
      ? editAgent.capabilities.filter((c) => c !== cap)
      : [...editAgent.capabilities, cap];
    setEditAgent({ ...editAgent, capabilities: caps });
  };

  const toggleTarget = (t: string) => {
    if (!editAgent) return;
    const targets = editAgent.handoff_targets.includes(t)
      ? editAgent.handoff_targets.filter((x) => x !== t)
      : [...editAgent.handoff_targets, t];
    setEditAgent({ ...editAgent, handoff_targets: targets });
  };

  if (loading) return <div className="animate-pulse h-40 rounded-2xl bg-white border border-gray-100 max-w-3xl" />;
  if (error && agents.length === 0) return <div className="rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-xs font-semibold text-rose-700 max-w-3xl">{error}</div>;

  return (
    <div className="space-y-3 max-w-3xl">
      {msg && <p className="text-xs font-semibold text-emerald-700">{msg}</p>}
      {error && <p className="text-xs font-semibold text-rose-600">{error}</p>}
      {!canManage && <p className="text-xs text-gray-400">لديك صلاحية عرض فقط — إدارة الموظفين تتطلب صلاحية «إدارة موظفي الذكاء الاصطناعي».</p>}

      {canManage && !creating && (
        <div className="flex justify-end">
          <button type="button" onClick={() => { setCreating(true); setFormError(''); }}
            className="px-4 py-2 rounded-xl bg-teal-600 text-white text-xs font-bold hover:bg-teal-700">
            + إنشاء موظف ذكي
          </button>
        </div>
      )}

      {creating && (
        <div className="rounded-2xl bg-white border border-teal-100 p-5 space-y-3">
          <h3 className="text-sm font-black text-gray-900">إنشاء موظف ذكي جديد</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-gray-600 mb-1">الاسم</label>
              <input value={newAgent.name} onChange={(e) => setNewAgent({ ...newAgent, name: e.target.value })}
                className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm" placeholder="مثال: مساعد الشكاوى" />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-600 mb-1">المعرّف (إنجليزي)</label>
              <input value={newAgent.assistant_key} onChange={(e) => setNewAgent({ ...newAgent, assistant_key: e.target.value })}
                dir="ltr" className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm" placeholder="complaints_2" />
            </div>
          </div>
          {formError && <p className="text-xs font-semibold text-rose-600">{formError}</p>}
          <div className="flex gap-2">
            <button type="button" disabled={saving} onClick={() => void createAgent()}
              className="px-4 py-2 rounded-xl bg-gray-900 text-white text-xs font-bold hover:bg-gray-800 disabled:opacity-50">
              {saving ? 'جارٍ الإنشاء…' : 'إنشاء'}
            </button>
            <button type="button" onClick={() => setCreating(false)}
              className="px-4 py-2 rounded-xl border border-gray-200 text-xs font-bold text-gray-700">إلغاء</button>
          </div>
        </div>
      )}

      <div className="rounded-2xl bg-white border border-gray-100 overflow-hidden">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-gray-100 text-left text-gray-400">
              <th className="px-5 py-3 font-bold">الموظف</th>
              <th className="px-5 py-3 font-bold">القسم</th>
              <th className="px-5 py-3 font-bold">الحالة</th>
              <th className="px-5 py-3 font-bold">عالَج اليوم</th>
              <th className="px-5 py-3 font-bold">إجراءات</th>
            </tr>
          </thead>
          <tbody>
            {agents.map((a) => (
              <tr key={a.id} className="border-b border-gray-50 last:border-0">
                <td className="px-5 py-3">
                  <div className="font-bold text-gray-800">{a.name}</div>
                  <div className="text-[10px] text-gray-400">{ROLE_LABELS[a.role] ?? a.role}</div>
                </td>
                <td className="px-5 py-3 text-gray-500">{a.department ?? '—'}</td>
                <td className="px-5 py-3">
                  <span className={`inline-flex items-center gap-1.5 font-bold ${a.status === 'active' ? 'text-emerald-700' : 'text-amber-600'}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${a.status === 'active' ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                    {a.status === 'active' ? 'نشط' : 'موقوف'}
                  </span>
                </td>
                <td className="px-5 py-3 font-bold text-gray-800">{a.handled_today}</td>
                <td className="px-5 py-3">
                  <div className="flex items-center gap-2">
                    {canManage && (
                      <>
                        <button type="button" onClick={() => { setEditAgent(a); setFormError(''); }} className="px-2.5 py-1 rounded-lg border border-gray-200 text-gray-600 font-bold hover:bg-gray-50">تعديل</button>
                        <button type="button" disabled={saving} onClick={() => void toggleStatus(a)} className={`px-2.5 py-1 rounded-lg font-bold disabled:opacity-50 ${a.status === 'active' ? 'border border-amber-200 text-amber-700 hover:bg-amber-50' : 'border border-emerald-200 text-emerald-700 hover:bg-emerald-50'}`}>
                          {a.status === 'active' ? 'إيقاف' : 'تفعيل'}
                        </button>
                      </>
                    )}
                    <button type="button" onClick={() => void openActivity(a)} className="px-2.5 py-1 rounded-lg border border-gray-200 text-gray-600 font-bold hover:bg-gray-50">النشاط</button>
                  </div>
                </td>
              </tr>
            ))}
            {agents.length === 0 && (
              <tr><td colSpan={5} className="px-5 py-6 text-center text-gray-400">لا يوجد موظفون مسجلون.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editAgent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={() => setEditAgent(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-sm font-black text-gray-900">تعديل الموظف: {editAgent.name}</h3>

            <div>
              <label className="block text-xs font-bold text-gray-600 mb-1">الاسم</label>
              <input value={editAgent.name} onChange={(e) => setEditAgent({ ...editAgent, name: e.target.value })} className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-600 mb-1">القسم</label>
              <input value={editAgent.department ?? ''} onChange={(e) => setEditAgent({ ...editAgent, department: e.target.value || null })} className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-600 mb-1">الشخصية</label>
              <input value={editAgent.personality ?? ''} onChange={(e) => setEditAgent({ ...editAgent, personality: e.target.value || null })} className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-600 mb-1">المهمة</label>
              <textarea value={editAgent.mission ?? ''} rows={2} onChange={(e) => setEditAgent({ ...editAgent, mission: e.target.value || null })} className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm" />
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-600 mb-2">القدرات المسموح بها</label>
              <div className="grid grid-cols-2 gap-1.5">
                {KNOWN_CAPABILITIES.map((c) => (
                  <label key={c.key} className="flex items-center gap-2 text-xs text-gray-700">
                    <input type="checkbox" checked={editAgent.capabilities.includes(c.key)} onChange={() => toggleCap(c.key)} className="rounded" />
                    {c.label}
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-600 mb-2">أهداف التحويل المسموح بها</label>
              <div className="flex flex-wrap gap-2">
                {agents.filter((x) => x.id !== editAgent.id).map((x) => (
                  <label key={x.id} className="flex items-center gap-1.5 text-xs text-gray-700 border border-gray-200 rounded-lg px-2 py-1">
                    <input type="checkbox" checked={editAgent.handoff_targets.includes(x.role)} onChange={() => toggleTarget(x.role)} className="rounded" />
                    {x.name}
                  </label>
                ))}
              </div>
            </div>

            {formError && <p className="text-xs font-semibold text-rose-600">{formError}</p>}
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={() => setEditAgent(null)} className="px-4 py-2 rounded-xl border border-gray-200 text-xs font-bold text-gray-600">إلغاء</button>
              <button type="button" disabled={saving} onClick={() => void saveAgent()} className="px-4 py-2 rounded-xl bg-gray-900 text-white text-xs font-bold disabled:opacity-50">
                {saving ? 'جارٍ الحفظ…' : 'حفظ'}
              </button>
            </div>
          </div>
        </div>
      )}

      {activityAgent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={() => setActivityAgent(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto p-6 space-y-3" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-sm font-black text-gray-900">سجل نشاط: {activityAgent.name}</h3>
            {activityLoading && <div className="animate-pulse h-24 rounded-xl bg-gray-50" />}
            {!activityLoading && activity.length === 0 && <p className="text-xs text-gray-400 text-center py-6">لا يوجد نشاط مسجل بعد.</p>}
            {!activityLoading && activity.map((ev) => (
              <div key={ev.id} className="rounded-xl border border-gray-100 px-4 py-2.5 text-xs">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-gray-800">{ev.tool} — {ev.action}</span>
                  <span className={`font-black ${ev.result === 'success' ? 'text-emerald-700' : 'text-rose-600'}`}>{ev.result === 'success' ? 'نجاح' : 'فشل'}</span>
                </div>
                <div className="mt-1 text-gray-400">
                  محادثة #{ev.conversation_id ?? '—'} · عميل #{ev.customer_id ?? '—'} · {ev.created_at}
                </div>
              </div>
            ))}
            <div className="flex justify-end pt-1">
              <button type="button" onClick={() => setActivityAgent(null)} className="px-4 py-2 rounded-xl border border-gray-200 text-xs font-bold text-gray-600">إغلاق</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function SmartAssistantSettingsPage() {
  const { can } = usePlatformPermissions();
  const [tab, setTab] = useState<TabKey>('whatsapp');
  const [waStatus, setWaStatus] = useState<WhatsAppStatus | null>(null);

  const canUpdate = can('intelligence.settings.update');
  const canConnect = can('intelligence.whatsapp.connect');
  const canDisconnect = can('intelligence.whatsapp.disconnect');
  const canManageAgents = can('intelligence.agents.manage');

  return (
    <IntelligenceShell>
      <div className="space-y-6">
      <div>
        <p className="text-xs text-gray-500 mt-1">
          إدارة المساعد الذكي لـ DressnMore وقناة واتساب الخاصة به.
          {waStatus && (
            <span className={`ml-2 inline-flex items-center gap-1.5 font-bold ${waStatus.session.status === 'connected' ? 'text-emerald-700' : 'text-rose-600'}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${waStatus.session.status === 'connected' ? 'bg-emerald-500' : 'bg-rose-500'}`} />
              WhatsApp {waStatus.session.status === 'connected' ? `متصل (${waStatus.session.phone ?? ''})` : 'غير متصل'}
            </span>
          )}
        </p>
      </div>

      <div className="flex flex-wrap gap-1 border-b border-gray-100">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-bold rounded-t-xl border-b-2 -mb-px transition-colors ${
              tab === t.key ? 'border-gray-900 text-gray-900' : 'border-transparent text-gray-400 hover:text-gray-600'
            }`}
          >
            <i className={t.icon} />
            {t.label}
            {t.soon && <span className="text-[9px] font-black text-gray-300">قريبًا</span>}
          </button>
        ))}
      </div>

      {tab === 'general' && <GeneralTab canUpdate={canUpdate} />}
      {tab === 'whatsapp' && (
        <WhatsAppConnectionCard canConnect={canConnect} canDisconnect={canDisconnect} onStatusChange={setWaStatus} />
      )}
      {tab === 'agents' && <AgentsTab canManage={canManageAgents} />}
      {tab === 'behavior' && <ComingSoon title="سلوك المساعد" />}
      {tab === 'notifications' && <ComingSoon title="الإشعارات" />}
      {tab === 'security' && <ComingSoon title="الأمان" />}
      </div>
    </IntelligenceShell>
  );
}
